import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Divider,
  Input,
  InputRef,
  Popover,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  TableColumnType,
  Tag,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { Key, useEffect, useRef, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import AddUser from "./AddUser";
import UsersService, {
  SelectClientsType,
  UserTableRoleTypeOption,
} from "../../Services/UsersService";
import {
  TextSearchTableEnum,
  TextSearchTableOption,
} from "../../Common/SearchTableOptions";
import { FilterDropdownProps } from "antd/es/table/interface";
import search from "../../Images/search.svg";

export interface IDataType {
  key: Key;
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  role: any;
  accessSettings: any;
  clientSpecificRole: boolean;
  roleClientsType: SelectClientsType;
  type: ITableRoleType;
  clientRoleList: IClientRoleList[];
}

interface IClientIdName {
  id?: string;
  bName?: string;
}

interface IClientRoleList {
  id: IClientIdName[];
  roleId: string;
  isClientRole: boolean;
  roleName: string;
}

export enum ITableRoleType {
  AllClients,
  LimitedClients,
  ClientSpecificRole,
}

const Users = () => {
  type DataIndex = keyof IDataType;
  const { themeData } = useTheme();
  const searchInput = useRef<InputRef>(null);
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [userList, setUserList] = useState<any[]>();
  const [userId, setUserId] = useState<string | undefined>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [filter, setfilter] = useState<{ sortCol: string; sortDir: string }>({
    sortCol: "",
    sortDir: "",
  });
  const [width, setWidth] = useState<any>(window.innerWidth);

  const [mainSearch, setMainSearch] = useState<string>("");
  const [openDropdown, setOpenDropdown] = useState<DataIndex | null>(null);
  const [searchColumnCount, setSearchColumnCount] = useState<number>(0);
  const [searchColumns, setSearchColumns] = useState<any>({
    name: "",
    email: "",
    phoneNumber: "",
    type: null,
  });
  const [searchColumnsDropDown, setSearchColumnsDropDown] = useState<{
    nameDropEnum: TextSearchTableEnum;
    phoneNumberDropEnum: TextSearchTableEnum;
    emailDropEnum: TextSearchTableEnum;
  }>({
    nameDropEnum: TextSearchTableEnum.contains,
    phoneNumberDropEnum: TextSearchTableEnum.contains,
    emailDropEnum: TextSearchTableEnum.contains,
  });

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [width]);

  useEffect(() => {
    const requestModel = {
      name: searchColumns.name?.trim(),
      nameDropEnum: searchColumnsDropDown.nameDropEnum,
      email: searchColumns.email?.trim(),
      emailDropEnum: searchColumnsDropDown.emailDropEnum,
      phoneNo: searchColumns.phoneNumber?.trim(),
      PhoneNoDropEnum: searchColumnsDropDown.phoneNumberDropEnum,
      tableRoleType: searchColumns.type,
    };
    getUserList(
      (page - 1) * pageSize,
      pageSize,
      mainSearch,
      filter?.sortCol,
      filter?.sortDir,
      requestModel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, page, mainSearch, pageSize, filter, searchColumns]);

  const getUserList = async (
    start: number,
    length: number,
    search: string,
    sortCol: string,
    sortDir: string,
    requestModel?: any
  ) => {
    setLoading(true);
    let res = await UsersService?.getUserList(
      start,
      length,
      search,
      sortCol,
      sortDir,
      requestModel
    );
    if (res?.result?.items) {
      setUserList(
        res?.result?.items.map((d: any) => {
          return {
            id: d?.id,
            name: d?.name?.first + " " + (d?.name?.last ? d?.name?.last : ""),
            phoneNumber: d?.phoneNumber,
            type: d?.clientSpecificRole
              ? ITableRoleType.ClientSpecificRole
              : d?.roleClientsType === SelectClientsType.AllClients
              ? ITableRoleType.AllClients
              : ITableRoleType.LimitedClients,
            email: d?.email,
            role: d?.role,
            clientRoleList: d?.clientRoleList,
          };
        })
      );
      setTotalRecords(res.result?.totalRecords);
      setLoading(false);
    }
  };

  const handleReset = (
    clearFilters: () => void,
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    clearFilters();
    confirm();

    let prevValue = searchColumns[dataIndex];

    // console.log("newValue111", prevValue, dataIndex);

    if (prevValue) {
      setSearchColumnCount((prev) => prev - 1);
    }
    if (prevValue) {
      setSearchColumns((prev: any) => {
        const newSearchColumns = { ...prev };
        delete newSearchColumns[dataIndex];
        return newSearchColumns;
      });
    }
  };

  const handleSearch = (
    selectedKeys: string[] | any,
    confirm: any,
    dataIndex: DataIndex
  ) => {
    // console.log("handleSearch", dataIndex, selectedKeys, searchColumns);

    let prevValue = searchColumns[dataIndex];
    let newValue = selectedKeys;

    console.log("newValue", prevValue, newValue);
    if (newValue?.length < 0) {
      confirm();
      setOpenDropdown(null);
      return;
    }

    let newSearchColumns = { ...searchColumns };
    console.log("newSearchColumns", newSearchColumns);

    switch (dataIndex) {
      case "name":
      case "phoneNumber":
      case "email":
        newSearchColumns[dataIndex] = selectedKeys[0];
        break;
      case "type":
        newSearchColumns[dataIndex] = selectedKeys;
        break;
    }

    if (!prevValue && newValue?.length > 0) {
      setSearchColumnCount((prev) => prev + 1);
    }

    setSearchColumns(newSearchColumns);
    // console.log(searchColumnCount, "count", prevValue, newValue, selectedKeys);
    confirm();
    setOpenDropdown(null); // Close the dropdown
  };

  // console.log("User List", userList);
  const handleDropdownText = (
    dataIndex: DataIndex,
    valDropDown: TextSearchTableEnum
  ) => {
    switch (dataIndex) {
      case "name":
        setSearchColumnsDropDown({
          ...searchColumnsDropDown,
          nameDropEnum: valDropDown,
        });
        break;
      case "phoneNumber":
        setSearchColumnsDropDown({
          ...searchColumnsDropDown,
          phoneNumberDropEnum: valDropDown,
        });
        break;
      case "email":
        setSearchColumnsDropDown({
          ...searchColumnsDropDown,
          emailDropEnum: valDropDown,
        });
        break;
    }
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex,
    isNumber: boolean,
    searchPlaceHolder?: string
  ): TableColumnType<IDataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {dataIndex === "type" ? (
          <Checkbox.Group
            options={UserTableRoleTypeOption?.map((x: any) => ({
              value: x.value,
              label: x.label,
            }))}
            style={{
              marginBottom: 8,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
            // value={searchColumns.gstRate}
            value={selectedKeys}
            onChange={(e: any) => {
              setSelectedKeys(e ? [...e] : []);
            }}
          />
        ) : (
          <>
            {
              <Select
                defaultValue={TextSearchTableEnum.contains}
                style={{
                  width: "100%",
                  maxWidth: "200px",
                  marginBottom: "8px",
                }}
                onChange={(val: TextSearchTableEnum) => {
                  // setExportItemsData([]);
                  handleDropdownText(dataIndex, val);
                  // setTextTypeSearchColumn(val);
                  // setType(1);
                }}
                options={TextSearchTableOption?.map((x: any) => ({
                  value: x.value,
                  label: x.label,
                }))}
              />
            }
            <Input
              ref={searchInput}
              placeholder={`Search.. ${searchPlaceHolder}`}
              value={selectedKeys[0]}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() =>
                handleSearch(selectedKeys as string[], confirm, dataIndex)
              }
              style={{ marginBottom: 8, display: "block", width: "200px" }}
            />
          </>
        )}
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            // icon={<SearchOutlined />}

            size="small"
            style={{ width: 95 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters, confirm, dataIndex);
            }}
            size="small"
            style={{ width: 95 }}
          >
            Clear
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) =>
      searchPlaceHolder ? (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ) : (
        <FilterOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    filterDropdownOpen: openDropdown === dataIndex,
    filterDropdownProps: {
      placement: "bottomLeft",
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setOpenDropdown(dataIndex);
        setTimeout(() => {
          searchInput.current?.select(); // Use select for Input component
        }, 100);
      } else {
        setOpenDropdown(null);
      }
    },
  });

  const columns: ColumnsType<IDataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "Name.First",
      width: "10%",
      sorter: true,
      defaultSortOrder: "ascend",
      render: (_: string, record: IDataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record.name}
        </Skeleton>
      ),
      ...getColumnSearchProps("name", false, "Name"),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "PhoneNumber",
      width: "10%",
      sorter: true,
      render: (_: string, record: IDataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record?.phoneNumber}
        </Skeleton>
      ),
      ...getColumnSearchProps("phoneNumber", false, "Phone Number"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "Email",
      width: "15%",
      sorter: true,
      render: (_: string, record: IDataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record.email}
        </Skeleton>
      ),
      ...getColumnSearchProps("email", false, "Email"),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "15%",
      render: (_: string, record: IDataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record.type === ITableRoleType.AllClients
            ? "All Clients"
            : record.type === ITableRoleType.LimitedClients
            ? "Limited Clients"
            : "Client Specific Role"}
        </Skeleton>
      ),
      ...getColumnSearchProps("type", false),
    },
    {
      title: "Role",
      dataIndex: "clientRoleList",
      key: "clientRoleList",
      width: "15%",
      render: (_: any, record: IDataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {record.type === ITableRoleType.AllClients ? (
            record.clientRoleList[0].roleName
          ) : record.type === ITableRoleType.LimitedClients ? (
            <Row gutter={4}>
              <Col>{record.clientRoleList[0].roleName}</Col>
              <Col>
                <Popover
                  arrow={{ pointAtCenter: true }}
                  placement="rightBottom"
                  title={
                    <>
                      <Row gutter={4}>
                        <Col>Client List</Col>
                        <Col>
                          <Badge count={record.clientRoleList[0]?.id.length} />
                        </Col>
                      </Row>
                      <Divider className="my-5" />
                    </>
                  }
                  content={
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      {record.clientRoleList[0]?.id?.map((x, index) => {
                        return (
                          <Row gutter={4}>
                            <Col>{index + 1}.</Col>
                            <Col>{x.bName}</Col>
                          </Row>
                        );
                      })}
                    </div>
                  }
                >
                  <Tag>Client List</Tag>
                </Popover>
              </Col>
            </Row>
          ) : (
            <Row gutter={4}>
              <Col>
                <Popover
                  arrow={{ pointAtCenter: true }}
                  placement="rightBottom"
                  title={
                    <>
                      <Row gutter={4} style={{ fontWeight: 700 }}>
                        <Col>Role - Client List</Col>
                        <Col>
                          <Badge count={record.clientRoleList?.length} />
                        </Col>
                      </Row>
                      <Divider className="my-5" />
                    </>
                  }
                  content={
                    <div
                      style={{
                        maxHeight: 200,
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      {record?.clientRoleList?.map((x, index) => {
                        return (
                          <Row gutter={4}>
                            <Col>{index + 1}.</Col>
                            <Col>
                              {x?.roleName} - {x?.id[0]?.bName}
                            </Col>
                          </Row>
                        );
                      })}
                    </div>
                  }
                >
                  <Tag>Role & Client List</Tag>
                </Popover>
              </Col>
            </Row>
          )}
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "8%",
      render: (_: any, record: any) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={12}>
            <>
              <Col>
                <EditOutlined
                  onClick={() => {
                    // navigate("/billing/users/userDetails", {
                    //   state: {
                    //     userId: record?.id,
                    //   },
                    // });
                    setUserId(record?.id);
                    setIsModelOpen(true);
                  }}
                />
              </Col>
              {/* <Col>
                <EyeOutlined />
              </Col>

              <Col>
                <DeleteOutlined style={{ color: "red" }} />
              </Col> */}
            </>
          </Row>
        </Skeleton>
      ),
    },
  ];

  //----- Table Ascending & Desending Order -----
  const tableSort = (pagination?: any, sort?: any, filter?: any) => {
    setfilter({
      sortCol: filter.column !== undefined ? filter.columnKey : "number",
      sortDir:
        filter.column !== undefined
          ? filter.order === "descend"
            ? "desc"
            : "asc"
          : "desc",
    });
    // console.log("sort1111111111111", sort?.TaxRate?.Rate, sort);
  };

  return (
    <>
      <div
        style={{
          padding: "0px 0px 3px 0px",
        }}
      >
        {/* <Card
          size="small"
          className="TableCard"
          style={{
            width: "100%",
            height: "100%",
            // minHeight: "60vh",
            // marginLeft: "16px",
            boxShadow: "0px 0px 10px 0px #96A1C84D",
            // marginTop: "30px",
            marginTop: "15px",
            border: "1px solid #eef2fe",
          }}
        ></Card> */}
        {/* <Card
          size="small"
          className="TableCard"
          style={{
            justifyContent: "center",
            margin: "15px 15px 0px 15px",
            boxShadow: "0px 0px 10px 0px #96A1C84D",
            height: "90vh",
          }}
        > */}

        <Row justify="space-between">
          <Col>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => {
                setIsModelOpen(true);
                setUserId(undefined);
              }}
            >
              Add User
            </Button>
          </Col>

          <Col
            style={{
              marginTop: width <= 768 ? 15 : "",
            }}
          >
            <Row gutter={width <= 768 ? 98 : 8}>
              <Col>
                <Input
                  allowClear
                  style={{ width: "200px", border: "1px solid #f1f3ff" }}
                  placeholder="Search.."
                  onChange={(e: any) => {
                    setLoading(true);
                    // setExportItemsData([]);
                    if (!e.cancelable) {
                      //console.log("typeing user............");
                      const searchItem = setTimeout(() => {
                        setPage(1);
                        setMainSearch(e.target.value);
                      }, 1000);
                      return () => clearTimeout(searchItem);
                    } else {
                      // console.log("Click cancle............");
                      setMainSearch(e.target.value);
                    }
                  }}
                  suffix={
                    loading || mainSearch ? (
                      ""
                    ) : (
                      <img alt="Search.." src={search} />
                    )
                  }
                />
              </Col>
            </Row>
          </Col>
          {/* <Col>
              <span style={{ marginRight: "5px" }}>Select Role :</span>
              <Select
                style={{ width: "180px" }}
                allowClear={true}
                options={[
                  {
                    value: ApplicationRoleNames?.ADMIN,
                    label: "Admin",
                  },
                  {
                    value: ApplicationRoleNames?.AUTHORITIES,
                    label: "Authorities",
                  },
                  {
                    value: ApplicationRoleNames?.DATAENTRY,
                    label: "Data Entry",
                  },
                  {
                    value: ApplicationRoleNames?.AUDITORS,
                    label: "Auditors",
                  },
                  {
                    value: ApplicationRoleNames?.VIEWONLY,
                    label: "View Only",
                  },
                ]}
                onChange={(val: any) => setSelectRole(val)}
              />
            </Col> */}
        </Row>

        <div style={{ marginTop: "0px" }}>
          <Table
            columns={columns}
            className={`Tabel-style table-${
              themeData?.componentSize ?? "middle"
            }`}
            onChange={tableSort}
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            dataSource={
              loading
                ? (Array.from({ length: pageSize }, (_, index) => ({
                    key: `loading-${index}`,
                  })) as IDataType[])
                : userList
            }
            pagination={{
              total: totalRecords,
              current: page,
              pageSize: pageSize,
              showSizeChanger: true,
              onChange: (page: number, pageSize: number) => {
                setPage(page);
                setPageSize(pageSize);
                // setExportType(-1);
              },
              pageSizeOptions: ["10", "15", "25", "50", "100"],
              showTotal: (totalRecords, page) =>
                `${page[0]}-${page[1]} of ${totalRecords} items`,
            }}
            scroll={
              pageSize > 15
                ? { x: 1000, y: window.innerHeight - 320 }
                : { x: 1000 }
            }
          />
        </div>
        {/* </Card> */}
      </div>
      {isModelOpen && (
        <AddUser
          onCancel={() => setIsModelOpen(false)}
          isModelOpen={isModelOpen}
          editUserId={userId}
          setRefresh={setRefresh}
        />
      )}
    </>
  );
};

export default Users;
